import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Play, Image as ImageIcon, Pin, Heart } from "lucide-react";
import { fileUrl, fmtViews, fmtDuration } from "../lib/api";
import { getResume } from "../lib/resume";

const ResumeBar = ({ id }) => {
  const r = getResume(id);
  if (!r?.d) return null;
  return (
    <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20" data-testid={`resume-bar-${id}`}>
      <div className="h-full bg-rose-600" style={{ width: `${Math.min(100, (r.t / r.d) * 100)}%` }} />
    </div>
  );
};

export const Thumb = ({ item, hoverPlay = true }) => {
  const [hover, setHover] = useState(false);
  const isVideo = item.type === "video";
  const src = item.has_thumb ? fileUrl(item.id, "thumb") : isVideo ? null : fileUrl(item.id);
  const startAt = Math.floor((item.duration || 0) * 0.2) || 1;
  // Preview video only loads while hovering, so cards never compete with the main player for bandwidth.
  return (
    <div className="absolute inset-0" onMouseEnter={() => hoverPlay && isVideo && setHover(true)} onMouseLeave={() => setHover(false)}>
      {src && <img src={src} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />}
      {isVideo && hover && (
        <video
          src={`${fileUrl(item.id)}#t=${startAt}`}
          muted
          playsInline
          autoPlay
          loop
          preload="auto"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </div>
  );
};

export const MediaCard = ({ item, onOpen, className = "", big = false }) => {
  const isVideo = item.type === "video";
  const inner = (
    <>
      <div className={`relative overflow-hidden bg-[#121117] ${big ? "aspect-video lg:aspect-auto lg:h-full" : "aspect-video"} ${!isVideo ? "!aspect-[4/5]" : ""}`}>
        <Thumb item={item} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="font-mono-d bg-black/70 px-2 py-1 text-[10px] tracking-widest text-white">{isVideo ? "HD" : "FOTO"}</span>
          {item.featured && <span className="font-mono-d bg-rose-600 px-2 py-1 text-[10px] tracking-widest text-white">DESTAQUE</span>}
          {item.pinned && <span className="font-mono-d flex items-center gap-1 bg-amber-400 px-2 py-1 text-[10px] tracking-widest text-black" data-testid={`card-pinned-${item.id}`}><Pin size={10} /> TOP</span>}
        </div>
        {isVideo && item.duration > 0 && (
          <span className="font-mono-d absolute bottom-3 right-3 bg-black/75 px-2 py-1 text-xs text-white">{fmtDuration(item.duration)}</span>
        )}
        {isVideo && <ResumeBar id={item.id} />}
        <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 scale-75 items-center justify-center rounded-full bg-rose-600/90 opacity-0 transition-[opacity,transform] duration-500 group-hover:scale-100 group-hover:opacity-100">
          {isVideo ? <Play size={20} fill="white" className="ml-0.5 text-white" /> : <ImageIcon size={20} className="text-white" />}
        </span>
        {big && (
          <div className="absolute bottom-6 left-6 right-24">
            <p className="eyebrow mb-2">{item.category || "Exclusivo"}</p>
            <h3 className="font-serif-d text-2xl font-semibold leading-tight text-white sm:text-4xl">{item.title}</h3>
          </div>
        )}
      </div>
      {!big && (
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-medium text-white transition-colors group-hover:text-rose-400">{item.title}</h3>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">{item.category || "Sem categoria"}</p>
          </div>
          <span className="font-mono-d flex shrink-0 items-center gap-3 text-xs text-slate-400" data-testid={`views-${item.id}`}>
            {item.likes > 0 && <span className="flex items-center gap-1 text-rose-400" data-testid={`likes-${item.id}`}><Heart size={12} className="fill-rose-500" /> {fmtViews(item.likes)}</span>}
            <span className="flex items-center gap-1"><Eye size={13} /> {fmtViews(item.views)}</span>
          </span>
        </div>
      )}
    </>
  );
  const cls = `group block text-left ${big ? "h-full" : ""} ${className}`;
  return isVideo ? (
    <Link to={`/video/${item.id}`} className={cls} data-testid={`media-card-${item.id}`}>{inner}</Link>
  ) : (
    <button type="button" onClick={() => onOpen?.(item)} className={`${cls} w-full`} data-testid={`media-card-${item.id}`}>{inner}</button>
  );
};
